import { chakra } from "@chakra-ui/react";
import { StyleImage } from "maplibre-gl";
import { useCallback, useRef } from "react";
import type { MapRef } from "react-map-gl/maplibre";

import { useMapListener } from "@nens/react/hooks";

interface SpriteSwatchProps {
  iconName: string;
  map: MapRef | undefined;
  isPattern?: boolean; // Pattern means an pattern overlaying the swatch, not a single icon
  rotation?: number;
}

const styleImageToCanvasCtx = (image: StyleImage) => {
  const canvas = document.createElement("canvas");
  canvas.width = image.data.width;
  canvas.height = image.data.height;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return;
  }

  ctx.putImageData(
    new ImageData(new Uint8ClampedArray(image.data.data), image.data.width, image.data.height),
    0,
    0
  );

  return canvas;
};

export function SpriteSwatch({ iconName, map, isPattern = false, rotation }: SpriteSwatchProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const lastIconNameRef = useRef<string | null>(null);

  const updateSwatch = useCallback(() => {
    const canvas = canvasRef.current;

    if (lastIconNameRef.current === iconName || !canvas || !map || !map.hasImage(iconName)) {
      return;
    }
    lastIconNameRef.current = iconName;

    const cssWidth = canvas.clientWidth;
    const cssHeight = canvas.clientHeight;

    // For patterns, we don't want to scale up the image, because it messes with the repeat pattern.
    // For icons, we want to scale up the image to make it look better.
    const multiplier = isPattern ? 1 : 2;

    const width = cssWidth * multiplier;
    const height = cssHeight * multiplier;

    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    ctx.clearRect(0, 0, width, height);

    const image = map.getImage(iconName);

    if (!image || image.data.width === 0 || image.data.height === 0) {
      console.warn(`SpriteSwatch: image for iconName "${iconName}" is missing or empty`);
      return;
    }

    const sourceCanvas = styleImageToCanvasCtx(image);
    if (!sourceCanvas) {
      return;
    }

    const drawPattern = () => {
      const pattern = ctx.createPattern(sourceCanvas, "repeat");
      if (!pattern) {
        return;
      }

      ctx.fillStyle = pattern;
      ctx.fillRect(0, 0, width, height);
    };

    const drawImage = () => {
      ctx.imageSmoothingEnabled = true;
      ctx.drawImage(sourceCanvas, 0, 0, image.data.width, image.data.height, 0, 0, width, height);
    };

    if (isPattern) {
      drawPattern();
    } else {
      drawImage();
    }
  }, [iconName, isPattern, map]);

  useMapListener("styledata", updateSwatch, map);
  updateSwatch();

  return (
    <chakra.canvas
      ref={canvasRef}
      flexShrink={0}
      w={4}
      h={4}
      title={iconName}
      transform={rotation != null ? `rotate(${rotation}deg)` : undefined}
      display="block"
      borderRadius={isPattern ? "base" : "none"}
    />
  );
}
